import styled, { css } from 'styled-components';

import { colors, typography } from '../../styles/tokens';

const fontTitle = 56;

export const Container = styled.div`
  width: 1199px;
  margin: 40px auto;

  @media (max-width: 1199px) {
    width: calc(100% - 40px);
    margin: 0 auto;
  }
`;

export const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 40px auto 0;

  padding: 8px 16px;
  border-radius: 999px;
  border: 1px solid ${colors.borderAccent};
  background: ${colors.surfaceAlt};

  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${colors.primaryHover};

  span {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: ${colors.primary};
    display: block;
  }
`;

export const Title = styled.h1`
  text-align: center;
  font-size: ${fontTitle}px;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: ${colors.textPrimary};

  margin: 24px 0 0;

  @media (max-width: 1199px) {
    font-size: ${fontTitle - 12}px;
  }
`;

export const Lead = styled.p`
  text-align: center;
  font-size: 19px;
  line-height: 1.65;
  color: ${colors.textSecondary};
  max-width: 720px;
  margin: 20px auto 0;

  @media (max-width: 1199px) {
    font-size: 16px;
  }
`;

export const CTARow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;
  margin: 32px 0 48px;
`;

const ctaBase = css`
  padding: 16px 30px;
  border-radius: 999px;
  font-family: ${typography.displayFontFamily};
  font-weight: 700;
  font-size: 16px;
  transition: all 0.2s;
`;

export const CTAPrimary = styled.a`
  ${ctaBase}
  background: ${colors.primary};
  color: ${colors.onPrimary};

  &:hover {
    background: ${colors.primaryHover};
  }
`;

export const CTASecondary = styled.a`
  ${ctaBase}
  border: 1px solid ${colors.borderAccent};
  color: ${colors.textPrimary};

  &:hover {
    border-color: ${colors.primary};
    color: ${colors.primary};
  }
`;

export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
  gap: 20px;
  margin-bottom: 60px;
`;

export const Card = styled.div<{ highlight?: boolean }>`
  border-radius: 30px;
  border: 1px solid
    ${props => (props.highlight ? colors.borderHighlight : colors.borderSubtle)};
  background: ${props =>
    props.highlight ? colors.surfaceHighlight : colors.surfaceSunken};
  padding: 30px;
  display: grid;
  gap: 10px;
  align-content: start;
`;

export const CardLabel = styled.div<{ highlight?: boolean }>`
  font-family: ${typography.monoFontFamily};
  font-size: 12px;
  letter-spacing: 0.14em;
  color: ${props => (props.highlight ? colors.primary : colors.textFaint)};
  text-transform: uppercase;
`;

export const CardTitle = styled.div`
  font-family: ${typography.displayFontFamily};
  font-weight: 700;
  font-size: 21px;
  color: ${colors.textPrimary};
`;

export const CardText = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: ${colors.textFaint};

  a {
    color: ${colors.primary};

    &:hover {
      color: ${colors.primaryHover};
    }
  }
`;

export const CardLink = styled.a`
  font-size: 17px;
  font-weight: 700;
  color: ${colors.primary};

  &:hover {
    color: ${colors.primaryHover};
  }
`;

export const SectionTitle = styled.h2`
  font-family: ${typography.displayFontFamily};
  font-weight: 800;
  font-size: 38px;
  line-height: 1.1;
  letter-spacing: -0.015em;
  color: ${colors.textPrimary};
  margin: 0;

  @media (max-width: 1199px) {
    font-size: 28px;
  }
`;

export const SectionSubtitle = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: ${colors.textSecondary};
  max-width: 700px;
  margin: 14px 0 40px;

  @media (max-width: 1199px) {
    font-size: 15px;
  }
`;

export const Section = styled.section`
  padding: 56px 0;
  border-top: 1px solid ${colors.borderSubtle};
`;

export const Track = styled.div`
  margin-bottom: 36px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const TrackTitle = styled.h3`
  font-family: ${typography.monoFontFamily};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${colors.primary};
  margin: 0 0 16px;
`;

export const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
  gap: 20px;
`;

export const Step = styled.div`
  border-radius: 28px;
  border: 1px solid ${colors.borderSubtle};
  background: ${colors.surfaceSunken};
  padding: 30px;
`;

export const StepNumber = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: ${colors.surfaceAccent};
  color: ${colors.primary};
  display: grid;
  place-items: center;
  font-family: ${typography.displayFontFamily};
  font-weight: 700;
  font-size: 15px;
`;

export const StepTitle = styled.h3`
  font-family: ${typography.displayFontFamily};
  font-weight: 600;
  font-size: 19px;
  margin: 18px 0 8px;
  color: ${colors.textPrimary};
`;

export const StepText = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.65;
  color: ${colors.textFaint};
`;

export const FAQList = styled.div`
  display: grid;
  gap: 12px;
`;

export const FAQItem = styled.details`
  border-radius: 24px;
  border: 1px solid ${colors.borderSubtle};
  background: ${colors.surfaceSunken};
  padding: 22px 26px;

  &[open] summary {
    color: ${colors.primary};
  }
`;

export const FAQQuestion = styled.summary`
  font-family: ${typography.displayFontFamily};
  font-weight: 600;
  font-size: 17px;
  color: ${colors.textPrimary};
  display: flex;
  gap: 16px;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }

  span:last-child {
    color: ${colors.primary};
    font-size: 20px;
    flex-shrink: 0;
  }
`;

export const FAQAnswer = styled.p`
  margin: 14px 0 0;
  font-size: 16px;
  line-height: 1.65;
  color: ${colors.textSecondary};

  a {
    color: ${colors.primary};

    &:hover {
      color: ${colors.primaryHover};
    }
  }
`;

export const Panel = styled.div`
  border-radius: 34px;
  border: 1px solid ${colors.border};
  background: ${colors.surfaceSunken};
  padding: 36px;

  @media (max-width: 1199px) {
    padding: 24px;
  }
`;

export const Form = styled.form`
  display: grid;
  gap: 20px;
`;

export const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 380px), 1fr));
  gap: 18px 22px;
`;

export const Field = styled.label`
  display: grid;
  gap: 8px;

  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${colors.textFaint};

  input,
  select,
  textarea {
    padding: 13px 16px;
    border-radius: 12px;
    border: 1px solid ${colors.borderAccent};
    background: ${colors.surface};
    color: ${colors.textPrimary};
    font-family: inherit;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;

    &:focus {
      border-color: ${colors.primary};
      outline: none;
    }
  }

  textarea {
    resize: vertical;
  }
`;

export const FormFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-top: 6px;
  padding-top: 24px;
  border-top: 1px solid ${colors.borderSubtle};
`;

export const SubmitButton = styled.button`
  padding: 15px 30px;
  border-radius: 999px;
  border: none;
  background: ${colors.primary};
  color: ${colors.onPrimary};
  font-family: ${typography.displayFontFamily};
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background: ${colors.primaryHover};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const FooterNote = styled.span`
  font-size: 14px;
  color: ${colors.textFaint};
  font-weight: 600;

  a {
    color: ${colors.primary};

    &:hover {
      color: ${colors.primaryHover};
    }
  }
`;

export const ErrorText = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${colors.danger};
`;

export const SuccessBox = styled.div`
  border: 1px solid ${colors.borderAccent};
  background: ${colors.surfaceAccent};
  border-radius: 18px;
  padding: 32px;

  div {
    font-size: 14px;
    font-weight: 800;
    letter-spacing: 0.08em;
    color: ${colors.primary};
    text-transform: uppercase;
  }

  p {
    margin: 12px 0 0;
    font-size: 16px;
    line-height: 1.6;
    color: ${colors.textPrimary};
  }
`;

export const WarningCallout = styled.div`
  border-radius: 24px;
  border-left: 3px solid ${colors.warning};
  background: ${colors.surfaceAlt};
  padding: 24px 28px;
  margin-top: 22px;
`;

export const WarningTitle = styled.div`
  font-family: ${typography.displayFontFamily};
  font-weight: 700;
  font-size: 17px;
  color: ${colors.warning};
  margin-bottom: 8px;
`;

export const WarningText = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.65;
  color: ${colors.textSecondary};
  max-width: 780px;
`;
